import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';

const PublicLocations = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [locationsData, setLocationsData] = useState({ section: null, locations: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await api.get(`/business/locations/public/${username}/`);
        // API returns array directly
        const locations = Array.isArray(res.data) ? res.data : (res.data.locations || res.data.items || []);
        setLocationsData({
          section: res.data.section || {},
          locations: locations
        });
      } catch (err) {
        console.error('Error fetching locations:', err);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchLocations();
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!locationsData.locations.length) {
    return (
      <div id="section-locations" style={{ padding: '4rem 2rem', textAlign: 'center', backgroundColor: '#0f172a' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)' }}>No locations available yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: '#0f172a' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
            {locationsData.section?.title || 'Our Locations'}
          </h2>
          {locationsData.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
              {locationsData.section.subtitle}
            </p>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {locationsData.locations.map((location, index) => (
            <div
              key={location.id || index}
              style={{
                padding: '2rem',
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '1rem',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <h3 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                {location.name}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {location.address && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <MapPin size={18} style={{ color: '#6366f1', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.5' }}>
                      {location.address}
                    </span>
                  </div>
                )}

                {location.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Phone size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                    <a href={`tel:${location.phone}`} style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                      {location.phone}
                    </a>
                  </div>
                )}

                {location.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Mail size={18} style={{ color: '#f59e0b', flexShrink: 0 }} />
                    <a href={`mailto:${location.email}`} style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                      {location.email}
                    </a>
                  </div>
                )}

                {location.hours && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Clock size={18} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                      {location.hours}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicLocations;
